import { useState } from 'react';
import { Box, Text, Dropdown, DropdownOption, ProgressSpinner, DropdownSearch } from '@bitrise/bitkit';
import partition from 'lodash/partition';

const findBranchesByText = (searchValue: string, branches: string[]) => {
  const lowerSearch = searchValue.toLowerCase();
  // exact matches by name
  const [exactMatchesByName, branchesWithoutExactNameMatch] = partition(
    branches,
    (name) => name.toLowerCase() === lowerSearch,
  );

  // startsWith matches by name
  const [startsWithMatchesByName, branchesWithoutAnyNameMatch] = partition(branchesWithoutExactNameMatch, (name) =>
    name.toLowerCase().startsWith(lowerSearch),
  );

  // substring matches in name
  const substringMatchesByName = branchesWithoutAnyNameMatch.filter((name) => name.toLowerCase().includes(lowerSearch));

  return [...exactMatchesByName, ...startsWithMatchesByName, ...substringMatchesByName];
};

const filterBranches = (searchValue: string, branches?: string[]): string[] | undefined => {
  let filteredBranches = branches || [];
  const trimmedSearchValue = searchValue.trim();
  if (trimmedSearchValue && filteredBranches) {
    filteredBranches = findBranchesByText(trimmedSearchValue, filteredBranches);
  }
  return filteredBranches || [];
};

interface BranchSelectDropdownProps {
  branches: string[];
  disabled: boolean;
  helperText?: string;
  infoTooltipLabel?: string;
  isLoading: boolean;
  label: string;
  onSelect: (branchName?: string) => void;
  value?: string;
  isRequired?: boolean;
}

export const BranchSelectDropdown = ({
  branches = [],
  disabled = false,
  helperText,
  infoTooltipLabel,
  isLoading = false,
  label,
  onSelect,
  value,
  isRequired,
}: BranchSelectDropdownProps): JSX.Element => {
  const [searchValue, setSearchValue] = useState('');
  const filteredBranches = filterBranches(searchValue, branches);

  return (
    <Dropdown
      helperText={helperText}
      onChange={({ target }) => onSelect(target.value || undefined)}
      label={label}
      infoTooltipLabel={infoTooltipLabel}
      value={value}
      search={branches.length > 5 ? <DropdownSearch value={searchValue} onChange={setSearchValue} /> : false}
      formLabel={
        <Box display="flex" gap="12">
          {isLoading ? <ProgressSpinner /> : null}
          {value || <Text color="text/secondary">Select an option</Text>}
        </Box>
      }
      disabled={disabled}
      flex="1"
      required={isRequired}
      width="calc(100% - 172px)"
    >
      {filteredBranches &&
        filteredBranches.map((name) => (
          <DropdownOption key={name} value={name}>
            {name}
          </DropdownOption>
        ))}
    </Dropdown>
  );
};
